/**
 * Requirement Parser Utilities
 * Handles detection and processing of requirement ID references
 */

/**
 * Requirement IDs in this app are `REQ-<digits>` with an optional domain prefix
 * (e.g. REQ-0001, SYS-REQ-002, HW-REQ-003). Anchoring on the literal `REQ-`
 * segment avoids false positives like COVID-19, ISO-9001 or UTF-8 that an
 * unrestricted /([A-Z]+-)+\d+/ would otherwise turn into "requirement" links.
 */
const REQUIREMENT_ID_REGEX = /\b(?:[A-Z]{1,6}-)?REQ-\d+\b/g;

/**
 * Strict regex for validating complete requirement ID strings
 */
const REQUIREMENT_ID_VALIDATION_REGEX = /^(?:[A-Z]{1,6}-)?REQ-\d+$/;

/**
 * Detect requirement IDs in text content
 * @param {string} text - Text to search for requirement IDs
 * @returns {Array} Array of detected requirement IDs
 */
export const detectRequirementIds = (text) => {
  if (!text || typeof text !== 'string') return [];

  const matches = text.match(REQUIREMENT_ID_REGEX);
  return matches ? [...new Set(matches)] : []; // Remove duplicates
};

/**
 * Validate if a string is a valid requirement ID
 * @param {string} id - Potential requirement ID
 * @returns {boolean} True if valid requirement ID
 */
export const validateRequirementId = (id) => {
  return REQUIREMENT_ID_VALIDATION_REGEX.test(id);
};

/**
 * Extract requirement IDs from chat messages
 * @param {Array} messages - Array of chat messages
 * @returns {Array} Array of unique requirement IDs found in messages
 */
export const extractRequirementIdsFromMessages = (messages) => {
  if (!Array.isArray(messages)) return [];

  const allIds = new Set();

  messages.forEach(message => {
    if (message.content) {
      const ids = detectRequirementIds(message.content);
      ids.forEach(id => allIds.add(id));
    }

    // Also check thinking steps if available
    if (message.thinkingSteps) {
      message.thinkingSteps.forEach(step => {
        if (step.content) {
          const ids = detectRequirementIds(step.content);
          ids.forEach(id => allIds.add(id));
        }
      });
    }
  });

  return Array.from(allIds);
};

/**
 * Replace requirement IDs in text with clickable links
 * @param {string} text - Text content
 * @param {Function} onRequirementClick - Callback when requirement link is clicked
 * @param {Object} linkProps - Additional props for link components
 * @returns {Array} Array of text and link elements
 */
export const renderTextWithRequirementLinks = (text, onRequirementClick, linkProps = {}) => {
  if (!text || typeof text !== 'string') return [text];

  const parts = [];
  let lastIndex = 0;

  text.replace(REQUIREMENT_ID_REGEX, (match, ...args) => {
    const matchIndex = args[args.length - 2]; // Second to last argument is index
    const matchLength = match.length;

    // Add text before the match
    if (matchIndex > lastIndex) {
      parts.push(text.slice(lastIndex, matchIndex));
    }

    // Add the requirement link
    parts.push({
      type: 'requirement-link',
      id: match,
      props: {
        onClick: () => onRequirementClick && onRequirementClick(match),
        ...linkProps
      }
    });

    lastIndex = matchIndex + matchLength;
    return match;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

/**
 * Get requirement link styles based on theme
 * @param {Object} theme - Material-UI theme object
 * @returns {Object} Styles for requirement links
 */
export const getRequirementLinkStyles = (theme) => ({
  color: (theme.vars || theme).palette.primary.main,
  textDecoration: 'underline',
  cursor: 'pointer',
  fontWeight: 'medium',
  '&:hover': {
    color: (theme.vars || theme).palette.primary.dark,
    textDecoration: 'underline',
  },
  padding: '2px 4px',
  borderRadius: '3px',
  backgroundColor: (theme.vars || theme).palette.background.default,
  border: `1px solid ${(theme.vars || theme).palette.divider}`,
});

/**
 * Format requirement ID for display
 * @param {string} id - Requirement ID
 * @returns {string} Formatted ID
 */
export const formatRequirementId = (id) => {
  return id.toUpperCase();
};

/**
 * Check if requirement exists in the system
 * @param {string} id - Requirement ID to check
 * @param {Array} requirements - Array of existing requirements
 * @returns {boolean} True if requirement exists
 */
export const requirementExists = (id, requirements = []) => {
  return requirements.some(req => req.id === id || req.requirement_id === id);
};